/**
 * Your screen share (Go Live) and camera, what you watch, and what is on
 * your screen now (docs/decisions/2026-10-29-screen-share-and-video.md
 * §2.1, §4.6). The voice controller hands it each new connection
 * (`liveAttach`), every message after the roster has it (`liveServer`) and
 * the end of a connection (`liveDetach`); the screens call the rest.
 *
 * The capture tracks live here, apart from any one connection, so an
 * automatic reconnect turns the camera back on, and the screen too while
 * its capture is still live (no new picker); a move turns the camera back
 * on in the new channel when it is allowed there, and ends a screen share
 * (Discord does). Leaving voice stops everything.
 */
import {
  Permissions,
  type Pid,
  type Snowflake,
  type VoiceMediaClaims,
  type VoiceMediaGrant,
  type VoiceMediaSource,
  type VoiceTokenClaims,
  type VoiceUnpublishReason,
  type VoiceVideoRef,
} from '@neosocial/protocol';
import { create } from 'zustand';
import { storage } from '../lib/storage';
import { useConfig } from '../store/config';
import { useData } from '../store/data';
import { can, channelPermissions } from '../store/selectors';
import {
  captureScreen,
  mediaCapsFrom,
  openCamera,
  publishCapsOf,
  refKey,
  sameRefs,
  VISIBLE_DEBOUNCE_MS,
  VISIBLE_MAX,
  type ScreenPick,
} from './media';
import type { VoiceAction } from './machine';
import { useVoice } from './store';
import {
  clampStreamVolume,
  MediaSupersededError,
  VoiceDeniedError,
  type ServerMessage,
  type StreamQuality,
  type Voice,
} from './types';

/** What the screens say about your media, as a toast (hub/streamUi.ts). */
export type MediaNotice =
  /** A publish or watch was refused (`denied` reason, or `unknown_request` from an older comms-voice). */
  | { kind: 'denied'; source: VoiceMediaSource; reason: string; limit: number }
  /** comms-voice ended your publication (`unpublished`). */
  | { kind: 'ended'; source: VoiceMediaSource; reason: VoiceUnpublishReason }
  /** The stream you tried to watch is gone. */
  | { kind: 'no_stream' }
  /** The camera or the capture could not open (`NotReadableError`, no device...). */
  | { kind: 'capture_failed'; source: VoiceMediaSource; name: string };

const noticeFns = new Set<(n: MediaNotice) => void>();
export function onMediaNotice(fn: (n: MediaNotice) => void): () => void {
  noticeFns.add(fn);
  return () => noticeFns.delete(fn);
}
function notify(n: MediaNotice): void {
  for (const fn of noticeFns) fn(n);
}

interface LocalCapture {
  video: MediaStreamTrack;
  audio: MediaStreamTrack | null;
  /** The sheet's pick (screen) */
  pick: ScreenPick | null;
  stream: MediaStream;
  off: () => void;
}

/** What the screens read about your own media and what the token grants. */
export interface LocalMediaState {
  /** Your capture's preview (a muted local stream), while you share or have the camera on. */
  screen: MediaStream | null;
  camera: MediaStream | null;
  /** The quality picked in place while live (null: the sheet's pick). */
  quality: StreamQuality | null;
  /** The sheet's pick of the live share. */
  pick: ScreenPick | null;
  /** The token's media claim: undefined from an older server (nothing may be published). */
  grant: VoiceMediaGrant | undefined;
  /** The token's STREAM channels (`claims.stream`). */
  streamChannels: Snowflake[] | undefined;
  /** The channels the token names at all (`claims.channels`). */
  tokenChannels: Snowflake[] | undefined;
  /**
   * A stream's own sound volume (0..2, `MAX_STREAM_VOLUME`) by the
   * streamer's user id, apart from their voice volume; remembered
   * (Discord's per-stream volume).
   */
  streamVolumes: Record<Snowflake, number>;
  /** Streams whose sound you muted, by the streamer's user id; remembered. */
  streamMutes: Record<Snowflake, true>;
  /** The screen you started watching last (the pop-out shows it first). */
  lastWatch: Pid | null;
}

/** Where the stream sound settings are remembered (this device). */
const STREAM_SOUND_KEY = 'stream-sound';

interface KeptStreamSound {
  volumes?: Record<Snowflake, number>;
  muted?: Record<Snowflake, true>;
}

function keptStreamSound(): Required<KeptStreamSound> {
  const k = storage.getJson<KeptStreamSound>(STREAM_SOUND_KEY, {});
  const volumes: Record<Snowflake, number> = {};
  for (const [id, v] of Object.entries(k.volumes ?? {}))
    if (typeof v === 'number' && Number.isFinite(v)) volumes[id] = clampStreamVolume(v);
  const muted: Record<Snowflake, true> = {};
  for (const [id, m] of Object.entries(k.muted ?? {})) if (m === true) muted[id] = true;
  return { volumes, muted };
}

const keptSound = keptStreamSound();

export const useLocalMedia = create<LocalMediaState>(() => ({
  screen: null,
  camera: null,
  quality: null,
  pick: null,
  grant: undefined,
  streamChannels: undefined,
  tokenChannels: undefined,
  streamVolumes: keptSound.volumes,
  streamMutes: keptSound.muted,
  lastWatch: null,
}));

let backend: Voice | null = null;
const local: Record<VoiceMediaSource, LocalCapture | null> = { screen: null, camera: null };
/** comms-voice ended the camera for a move: on again in the new channel when allowed. */
let cameraAfterMove = false;
/**
 * What each view has on screen: the voice room's stage, and the stream
 * pop-out (StreamMini) while the room is not. `visible` is both, the
 * room's first.
 */
export type VisibleFrom = 'room' | 'mini';
const visibleBy: Record<VisibleFrom, VoiceVideoRef[]> = { room: [], mini: [] };
let wantVisible: VoiceVideoRef[] = [];
let sentVisible: VoiceVideoRef[] = [];
let visTimer: ReturnType<typeof setTimeout> | undefined;

const act = (a: VoiceAction) => useVoice.getState().act(a);

/* --------------------------------- rules --------------------------------- */

/**
 * A flag the screens follow: the live config's when it says (a Console
 * change reaches you at once), else the token's grant, else the default
 * (`screen_share` on, `video` off).
 */
export function mediaFlagOn(
  key: 'screen_share' | 'video',
  grant: VoiceMediaGrant | undefined,
  flags: Record<string, boolean | undefined> | undefined = useConfig.getState().config?.flags as
    Record<string, boolean | undefined> | undefined,
): boolean {
  if (flags && key in flags) return flags[key] === true;
  const g = key === 'screen_share' ? grant?.screen : grant?.camera;
  return g ?? key === 'screen_share';
}

/**
 * STREAM in a channel you have no data for, by the token: its list where
 * the token names the channel; a hidden room a moderator moved you into,
 * which no token names, is comms-voice's to decide (the server pushed it
 * the grants there, Discord's moved-in permissions; a refusal says why).
 */
export function tokenStreamAllows(
  channelId: Snowflake,
  m: Pick<LocalMediaState, 'streamChannels' | 'tokenChannels'> = useLocalMedia.getState(),
): boolean {
  if (m.streamChannels?.includes(channelId)) return true;
  return !!m.tokenChannels && !m.tokenChannels.includes(channelId);
}

/** STREAM ("Video") in `channelId` for you: your roles there, else the token (tokenStreamAllows). */
export function streamAllowedIn(channelId: Snowflake | null): boolean {
  if (!channelId) return false;
  const d = useData.getState();
  const c = d.channels[channelId];
  // a DM call (2026-11-01) has no roles: the token says (every DM room, as Discord)
  if (c && c.guild_id) return can(channelPermissions(d, c), Permissions.STREAM);
  return tokenStreamAllows(channelId);
}

/** The caps now: the console's live limits over the token's (§2.2). */
export function currentCaps() {
  const settings = useConfig.getState().config?.settings as Record<string, unknown> | undefined;
  return mediaCapsFrom(settings, useLocalMedia.getState().grant?.caps);
}

/** The per-channel ceiling a `media_limit` refusal names. */
function limitOf(source: VoiceMediaSource): number {
  const c = currentCaps();
  return source === 'screen' ? c.streams_per_channel : c.cameras_per_channel;
}

/* ------------------------------ your captures ------------------------------ */

function keep(source: VoiceMediaSource, cap: LocalCapture | null): void {
  local[source] = cap;
  useLocalMedia.setState(
    source === 'screen'
      ? { screen: cap?.stream ?? null, pick: cap?.pick ?? null, quality: null }
      : { camera: cap?.stream ?? null },
  );
}

/** Stops a capture of yours (its tracks end; the browser's sharing bar goes). */
function endLocal(source: VoiceMediaSource): void {
  const c = local[source];
  if (!c) return;
  c.off();
  c.video.stop();
  c.audio?.stop();
  keep(source, null);
  if (source === 'camera') cameraAfterMove = false;
}

function capture(
  video: MediaStreamTrack,
  audio: MediaStreamTrack | null,
  pick: ScreenPick | null,
  source: VoiceMediaSource,
): LocalCapture {
  // the browser's own "Stop sharing", or a camera unplugged
  const ended = () => {
    if (local[source]?.video === video) stopMedia(source);
  };
  video.addEventListener?.('ended', ended);
  return {
    video,
    audio,
    pick,
    stream: new MediaStream([video]),
    off: () => video.removeEventListener?.('ended', ended),
  };
}

/** Asks comms-voice for the publication and adds its sections; a refusal stops the capture. */
async function publishLocal(source: VoiceMediaSource): Promise<boolean> {
  const v = backend;
  const c = local[source];
  if (!v || !c) return false;
  act({ type: 'pub_start', source, audio: !!c.audio });
  if (!v.media || useLocalMedia.getState().grant === undefined) {
    act({ type: 'pub_end', source });
    endLocal(source);
    notify({
      kind: 'denied',
      source,
      reason: v.media ? 'unknown_request' : 'unsupported',
      limit: limitOf(source),
    });
    return false;
  }
  // the share sheet's pick (or one picked in place since) holds the encoder
  // from the first frame: 480p sends 480p (QA D1)
  const lm = useLocalMedia.getState();
  const quality =
    source === 'screen'
      ? (lm.quality ?? (c.pick ? { height: c.pick.height, fps: c.pick.fps } : null))
      : null;
  try {
    await v.media.publish(source, { video: c.video, audio: c.audio }, quality);
    return true;
  } catch (e) {
    // stopped meanwhile, or a newer publish of it is the one that counts
    if (backend !== v || local[source] !== c || e instanceof MediaSupersededError) return false;
    act({ type: 'pub_end', source });
    endLocal(source);
    notify({
      kind: 'denied',
      source,
      reason: e instanceof VoiceDeniedError ? e.reason : 'unknown',
      limit: limitOf(source),
    });
    return false;
  }
}

/**
 * Go Live: opens the system picker with the sheet's pick, then publishes.
 * A cancelled picker does nothing. Resolves true once comms-voice said yes.
 */
export async function goLive(pick: ScreenPick): Promise<boolean> {
  const v = backend;
  if (!v) return false;
  if (local.screen) return true;
  let cap;
  try {
    cap = await captureScreen(pick);
  } catch (e) {
    const name = (e as Error)?.name ?? 'Error';
    // the person closed the picker: nothing to say
    if (name !== 'NotAllowedError' && name !== 'AbortError')
      notify({ kind: 'capture_failed', source: 'screen', name });
    return false;
  }
  if (backend !== v || local.screen) {
    cap.video.stop();
    cap.audio?.stop();
    return !!local.screen;
  }
  keep('screen', capture(cap.video, cap.audio, pick, 'screen'));
  return publishLocal('screen');
}

/** Turns the camera on (the chosen device, else the default) and publishes it. */
export async function cameraOn(): Promise<boolean> {
  const v = backend;
  if (!v || local.camera) return !!local.camera;
  let track: MediaStreamTrack;
  try {
    track = await openCamera(
      publishCapsOf('camera', currentCaps()),
      useVoice.getState().prefs.cameraDeviceId,
    );
  } catch (e) {
    notify({ kind: 'capture_failed', source: 'camera', name: (e as Error)?.name ?? 'Error' });
    return false;
  }
  if (backend !== v || local.camera) {
    track.stop();
    return !!local.camera;
  }
  keep('camera', capture(track, null, null, 'camera'));
  return publishLocal('camera');
}

/** Stop streaming, or the camera off: comms-voice first, then the capture ends. */
export function stopMedia(source: VoiceMediaSource): void {
  backend?.media?.unpublish(source);
  act({ type: 'pub_end', source });
  endLocal(source);
}

export function toggleCamera(): Promise<boolean> {
  if (local.camera) {
    stopMedia('camera');
    return Promise.resolve(false);
  }
  return cameraOn();
}

/** A lower (or the full) quality for the live share, in place (no message, §4.7). */
export function setLiveQuality(q: StreamQuality): void {
  if (!local.screen) return;
  backend?.media?.setQuality('screen', q);
  useLocalMedia.setState({ quality: q });
}

/* ------------------------------ watching ------------------------------ */

/**
 * Watch (or stop watching) `pid`'s screen. Resolves with the watch now.
 * Stopping is `watch off`: comms-voice frees its slots at once, so no more
 * of its video or sound comes (and its streamer pauses 3 s after its last
 * viewer).
 */
export async function watchStream(pid: Pid, on: boolean): Promise<boolean> {
  const v = backend;
  if (!v?.media) return false;
  try {
    const now = await v.media.watch(pid, on);
    if (now) useLocalMedia.setState({ lastWatch: pid });
    else if (useLocalMedia.getState().lastWatch === pid)
      useLocalMedia.setState({ lastWatch: null });
    return now;
  } catch (e) {
    if (e instanceof VoiceDeniedError && e.reason === 'no_stream') notify({ kind: 'no_stream' });
    else if (e instanceof VoiceDeniedError)
      notify({ kind: 'denied', source: 'screen', reason: e.reason, limit: limitOf('screen') });
    return false;
  }
}

/** Stop watching `pid`'s screen (the tile's, the focus view's, the menu's and the pop-out's). */
export function stopWatching(pid: Pid): Promise<boolean> {
  return watchStream(pid, false);
}

/** A stream's sound as you set it: its volume (0..2) and whether you muted it. */
export function streamSoundOf(
  userId: Snowflake,
  m: Pick<LocalMediaState, 'streamVolumes' | 'streamMutes'> = useLocalMedia.getState(),
): { volume: number; muted: boolean } {
  return { volume: m.streamVolumes[userId] ?? 1, muted: !!m.streamMutes[userId] };
}

/** What a stream's sound plays at: 0 while muted, else its volume. */
export function streamPlayVolume(sound: { volume: number; muted: boolean }): number {
  return sound.muted ? 0 : clampStreamVolume(sound.volume);
}

function keepStreamSound(): void {
  const { streamVolumes, streamMutes } = useLocalMedia.getState();
  storage.setJson(STREAM_SOUND_KEY, { volumes: streamVolumes, muted: streamMutes });
}

/**
 * A stream's own volume (0..2: up to 200 %, as Discord's), apart from the
 * person's voice volume; remembered per streamer on this device. Moving it
 * above 0 unmutes the stream, as Discord's slider does.
 */
export function setStreamVolume(userId: Snowflake, volume: number): void {
  const v = clampStreamVolume(volume);
  useLocalMedia.setState((s) => {
    const streamMutes = { ...s.streamMutes };
    if (v > 0) delete streamMutes[userId];
    return { streamVolumes: { ...s.streamVolumes, [userId]: v }, streamMutes };
  });
  keepStreamSound();
  applyStreamVolume(userId, true);
}

/** Mutes (or unmutes) a stream's sound for you, apart from the person's voice; remembered. */
export function setStreamMuted(userId: Snowflake, muted: boolean): void {
  useLocalMedia.setState((s) => {
    const streamMutes = { ...s.streamMutes };
    if (muted) streamMutes[userId] = true;
    else delete streamMutes[userId];
    // unmuting a stream left at 0 % brings it back at 100 % (Discord)
    const streamVolumes =
      !muted && s.streamVolumes[userId] === 0
        ? { ...s.streamVolumes, [userId]: 1 }
        : s.streamVolumes;
    return { streamMutes, streamVolumes };
  });
  keepStreamSound();
  // always: an unmute back to the default leaves nothing remembered, yet the
  // element still plays at 0 until told (QA NV-01)
  applyStreamVolume(userId, true);
}

/**
 * `userId`'s stream sound onto their connection. A stream you never set is
 * left alone unless `always` (a change you just made, which may be back to
 * the default).
 */
function applyStreamVolume(userId: Snowflake, always = false): void {
  const s = useVoice.getState().session;
  const lm = useLocalMedia.getState();
  if (!always && lm.streamVolumes[userId] === undefined && !lm.streamMutes[userId]) return;
  if (!backend?.media) return;
  const vol = streamPlayVolume(streamSoundOf(userId, lm));
  for (const m of Object.values(s.members))
    if (m.user_id === userId) backend.media.setStreamVolume(m.pid, vol);
}

/** Every remembered stream volume and mute, onto the connection's members. */
function applyStreamVolumes(): void {
  const lm = useLocalMedia.getState();
  for (const id of new Set([...Object.keys(lm.streamVolumes), ...Object.keys(lm.streamMutes)]))
    applyStreamVolume(id);
}

/** What a video slot carries for `pid`'s `source` now (to put in a `<video>`). */
export function remoteStream(pid: Pid, source: VoiceMediaSource): MediaStream | null {
  return backend?.media?.stream(pid, source) ?? null;
}

/* ------------------------------ what is on screen ------------------------------ */

function pageShown(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function flushVisible(): void {
  clearTimeout(visTimer);
  visTimer = undefined;
  const next = pageShown() ? wantVisible : [];
  if (sameRefs(next, sentVisible)) return;
  sentVisible = next;
  backend?.media?.setVisible(next);
}

/** The room's list, then the pop-out's, each video once, at most `VISIBLE_MAX`. */
export function mergeVisible(by: Record<VisibleFrom, readonly VoiceVideoRef[]>): VoiceVideoRef[] {
  const out: VoiceVideoRef[] = [];
  const seen = new Set<string>();
  for (const r of [...by.room, ...by.mini]) {
    const k = refKey(r);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ pid: r.pid, source: r.source });
  }
  return out.slice(0, VISIBLE_MAX);
}

/**
 * The videos on your screen now, most important first: the voice room
 * computes its own (tiles on screen, screens you watch, the focused one
 * first), the stream pop-out its one (`from: 'mini'`). Sent 250 ms after
 * the last change; `[]` at once while the page is hidden.
 */
export function setVisibleVideos(refs: VoiceVideoRef[], from: VisibleFrom = 'room'): void {
  visibleBy[from] = refs;
  wantVisible = mergeVisible(visibleBy);
  if (!pageShown() || !wantVisible.length) return flushVisible();
  clearTimeout(visTimer);
  visTimer = setTimeout(flushVisible, VISIBLE_DEBOUNCE_MS);
}

if (typeof document !== 'undefined')
  document.addEventListener('visibilitychange', () => {
    // hidden: nothing is received at once; shown again: after the debounce
    if (!pageShown()) flushVisible();
    else {
      clearTimeout(visTimer);
      visTimer = setTimeout(flushVisible, VISIBLE_DEBOUNCE_MS);
    }
  });

/* ------------------------------ the controller's hooks ------------------------------ */

/**
 * A connection was welcomed (a join, a move with a new token, or an
 * automatic reconnect: `reconnect`). Your camera comes back on when it is
 * still allowed; your screen only after a reconnect, while its capture is
 * still live.
 */
export function liveAttach(
  v: Voice,
  claims: (VoiceTokenClaims & VoiceMediaClaims) | null,
  o: { reconnect: boolean },
): void {
  backend = v;
  sentVisible = [];
  useLocalMedia.setState({
    grant: claims?.media,
    streamChannels: claims?.stream,
    tokenChannels: claims?.channels,
  });
  const ch = useVoice.getState().session.channelId;
  const s = local.screen;
  if (s) {
    if (o.reconnect && s.video.readyState === 'live' && screenAllowed(ch))
      void publishLocal('screen');
    else endLocal('screen');
  }
  const c = local.camera;
  if (c) {
    if (c.video.readyState === 'live' && cameraAllowed(ch)) void publishLocal('camera');
    else endLocal('camera');
  }
  if (wantVisible.length) flushVisible();
  // the streams' sound as you left it, on this connection's people
  applyStreamVolumes();
}

/** The connection went (a reconnect may follow; leaving stops your captures, below). */
export function liveDetach(): void {
  backend = null;
  sentVisible = [];
  clearTimeout(visTimer);
  visTimer = undefined;
}

function screenAllowed(ch: Snowflake | null): boolean {
  return mediaFlagOn('screen_share', useLocalMedia.getState().grant) && streamAllowedIn(ch);
}
function cameraAllowed(ch: Snowflake | null): boolean {
  return mediaFlagOn('video', useLocalMedia.getState().grant) && streamAllowedIn(ch);
}

/** Every message from comms-voice, after the roster took it. */
export function liveServer(e: ServerMessage): void {
  switch (e.t) {
    case 'unpublished':
      if (e.reason === 'moved' && e.source === 'camera' && local.camera) {
        cameraAfterMove = true;
        return;
      }
      endLocal(e.source);
      if (e.reason !== 'moved') notify({ kind: 'ended', source: e.source, reason: e.reason });
      return;
    case 'moved':
      if (cameraAfterMove) {
        cameraAfterMove = false;
        if (local.camera && cameraAllowed(e.channel)) void publishLocal('camera');
        else endLocal('camera');
      }
      return;
    case 'member':
      // a stream's volume follows the person to their new connection
      applyStreamVolume(e.user_id);
      return;
  }
}

// leaving voice, or a final failure, ends your captures
useVoice.subscribe((st, prev) => {
  const s = st.session.status;
  if (s === prev.session.status) return;
  if (s === 'idle' || s === 'failed') {
    endLocal('screen');
    endLocal('camera');
    wantVisible = [];
    useLocalMedia.setState({ lastWatch: null });
  }
});
